/**
 * America 250 AI Skills Promo — campaign state hook + UI components.
 *
 * Server is the single source of truth for dates. We hit
 * GET /api/promo/campaign-state and never expose the start/end window to
 * the client — the server returns only { active, is_final_day, code,
 * discount_pct, message_variant }.
 *
 * Re-fetch every 15 minutes so the banner flips to "final-day" copy on
 * July 10 and disappears after July 10 without a manual reload.
 */
import React, { useEffect, useMemo, useState, createContext, useContext } from "react";
import { apiClient } from "@/lib/api";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";

const CampaignContext = createContext({ active: false, is_final_day: false, code: null, discount_pct: 0, message_variant: "hidden" });

export function CampaignProvider({ children }) {
  const [state, setState] = useState({ active: false, is_final_day: false, code: null, discount_pct: 0, message_variant: "hidden" });

  useEffect(() => {
    let cancelled = false;
    async function fetchState() {
      try {
        const r = await apiClient.get("/promo/campaign-state");
        if (!cancelled) setState(r.data || { active: false, is_final_day: false });
      } catch (_e) {
        // Fail silent — banner just stays hidden.
      }
    }
    fetchState();
    const id = setInterval(fetchState, 15 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  return (
    <CampaignContext.Provider value={state}>{children}</CampaignContext.Provider>
  );
}

export function useCampaign() {
  return useContext(CampaignContext);
}

// ─── Top announcement bar ───────────────────────────────────────────────────
export function AmericaPromoTopBar() {
  const c = useCampaign();
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem("am250_top_dismissed") === "1"; } catch { return false; }
  });
  if (!c.active || dismissed) return null;

  const finalDay = c.is_final_day;
  // Short, single-line copy — fits cleanly on mobile + desktop without
  // truncating. The "Limited time offer" tagline is appended as a tasteful
  // secondary line that hides on the narrowest viewports so the headline
  // never gets cut.
  const copy = finalDay
    ? "Last Chance: 50% Off AI Prompt Dojo Ends Tonight"
    : "Happy 250th, America! 50% Off All Levels";
  const cta = finalDay ? "Claim 50% Off" : "Start Training";

  return (
    <div
      role="region"
      aria-label="America 250 AI Skills Promo"
      data-testid="am250-top-bar"
      className="relative w-full"
      style={{
        background: finalDay
          ? "linear-gradient(90deg, #0a0f1f 0%, #B91C1C 50%, #0a0f1f 100%)"
          : "linear-gradient(90deg, #0a0f1f 0%, #1e1b4b 35%, #B91C1C 65%, #0a0f1f 100%)",
        borderBottom: "1px solid rgba(212, 175, 55, 0.25)",
        color: "#fff",
      }}
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2 sm:py-2.5 flex items-center justify-between gap-2 sm:gap-3 text-xs sm:text-sm">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className="text-base sm:text-lg leading-none select-none flex-shrink-0" aria-hidden>🇺🇸</span>
          <div className="min-w-0 flex-1 flex flex-col sm:flex-row sm:items-baseline sm:gap-2.5">
            <span
              className="font-medium tracking-tight leading-tight sm:leading-none sm:truncate"
              data-testid="am250-top-copy"
            >
              {copy}
            </span>
            {!finalDay && (
              <span
                className="hidden sm:inline text-[10px] uppercase tracking-widest font-semibold whitespace-nowrap"
                style={{ color: "#D4AF37" }}
                data-testid="am250-top-tagline"
              >
                Limited time offer
              </span>
            )}
            {finalDay && (
              <span
                className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider"
                style={{ background: "#D4AF37", color: "#0a0f1f" }}
              >
                Tonight
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
          <Link
            to="/pricing"
            className="inline-flex items-center gap-1 font-semibold text-xs sm:text-sm whitespace-nowrap rounded-full px-2.5 sm:px-4 py-1 transition-colors"
            style={{
              background: "#fff",
              color: "#0a0f1f",
              borderTop: "1px solid rgba(212,175,55,0.6)",
            }}
            data-testid="am250-top-cta"
            data-track="pricing-cta"
          >
            <Sparkles size={12} /> {cta}
          </Link>
          <button
            type="button"
            aria-label="Dismiss promo banner"
            onClick={() => {
              setDismissed(true);
              try { sessionStorage.setItem("am250_top_dismissed", "1"); } catch (_e) { /* ignore */ }
            }}
            className="text-white/70 hover:text-white text-base leading-none px-1 -mr-1"
            data-testid="am250-top-dismiss"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Premium promo card (homepage / dashboard) ─────────────────────────────
export function AmericaPromoCard({ variant = "standard" }) {
  const c = useCampaign();
  if (!c.active) return null;
  const finalDay = c.is_final_day || variant === "final_day";

  const headline = finalDay
    ? "Last Chance to Build Your AI Advantage"
    : "America 250: Build Your AI Advantage";
  const body = finalDay
    ? "Today is the final day to claim 50% off every paid AI Prompt Dojo plan."
    : "Unlock 50% off every paid plan and start training the AI skill that helps you move faster, think sharper, and execute with more leverage.";
  const cta = finalDay ? "Claim 50% Off" : "Start Training";

  return (
    <section
      data-testid="am250-card"
      className="relative overflow-hidden rounded-2xl p-6 sm:p-8 lg:p-10"
      style={{
        background: "linear-gradient(135deg, #0a0f1f 0%, #11183b 55%, #1c1330 100%)",
        border: "1px solid rgba(212, 175, 55, 0.30)",
        color: "#fff",
        boxShadow: "0 22px 56px -28px rgba(10, 15, 31, 0.55)",
      }}
    >
      {/* Subtle accent: red→blue→gold thin line at the top */}
      <div
        className="absolute inset-x-0 top-0 h-[2px]"
        style={{
          background: "linear-gradient(90deg, #B91C1C 0%, #2563EB 50%, #D4AF37 100%)",
        }}
      />
      {/* Faint radial glow */}
      <div
        className="pointer-events-none absolute -top-24 -right-32 w-96 h-96 opacity-40"
        style={{
          background: "radial-gradient(closest-side, rgba(37, 99, 235, 0.45) 0%, rgba(37, 99, 235, 0) 70%)",
          filter: "blur(8px)",
        }}
        aria-hidden
      />

      <div className="relative flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-widest"
            style={{
              background: "rgba(212, 175, 55, 0.15)",
              color: "#D4AF37",
              border: "1px solid rgba(212, 175, 55, 0.45)",
            }}
            data-testid="am250-card-badge"
          >
            <Sparkles size={11} /> America 250
          </span>
          {finalDay && (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest"
              style={{ background: "#B91C1C", color: "#fff" }}
              data-testid="am250-card-final-day"
            >
              Final Day
            </span>
          )}
        </div>
        <div
          className="px-3 py-1.5 rounded-lg font-display tracking-tight"
          style={{
            background: "rgba(255, 255, 255, 0.06)",
            border: "1px solid rgba(255, 255, 255, 0.10)",
            color: "#fff",
            fontSize: "0.82rem",
          }}
          data-testid="am250-card-pct"
        >
          <span className="font-semibold text-xl mr-1" style={{ color: "#D4AF37" }}>50%</span>
          <span className="opacity-80">off</span>
        </div>
      </div>

      <h2
        className="relative mt-6 font-display tracking-tight"
        style={{ fontSize: "clamp(1.65rem, 3.2vw, 2.6rem)", lineHeight: 1.05 }}
        data-testid="am250-card-headline"
      >
        {headline}
      </h2>

      <p className="relative mt-3 text-sm sm:text-base max-w-2xl leading-relaxed" style={{ color: "rgba(255,255,255,0.78)" }} data-testid="am250-card-body">
        {body}
      </p>

      {!finalDay && (
        <p
          className="relative mt-4 italic text-sm max-w-xl"
          style={{ color: "rgba(212, 175, 55, 0.85)", borderLeft: "2px solid rgba(212, 175, 55, 0.45)", paddingLeft: 12 }}
          data-testid="am250-card-philosophy"
        >
          "Desire is a contract you make with yourself until you get what you want."
        </p>
      )}

      <div className="relative mt-6 flex flex-wrap items-center gap-3">
        <Link
          to="/pricing"
          data-testid="am250-card-cta"
          data-track="pricing-cta"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold transition-transform hover:scale-[1.02]"
          style={{
            background: "linear-gradient(135deg, #fff 0%, #f8fafc 100%)",
            color: "#0a0f1f",
            border: "1px solid rgba(212, 175, 55, 0.55)",
            boxShadow: "0 6px 20px -8px rgba(212, 175, 55, 0.6)",
          }}
        >
          {cta} <Sparkles size={14} />
        </Link>
        <span className="text-xs" style={{ color: "rgba(255,255,255,0.55)" }}>
          Use code <span className="font-mono font-semibold" style={{ color: "#fff" }}>{c.code || "AMERICA250"}</span> at checkout
        </span>
      </div>
    </section>
  );
}

// ─── Pricing page banner ────────────────────────────────────────────────────
export function AmericaPromoPricingBanner() {
  const c = useCampaign();
  if (!c.active) return null;
  const finalDay = c.is_final_day;
  const headline = finalDay ? "Final Day to Claim 50% Off" : "50% Off Every Paid Plan";
  const body = finalDay
    ? "Your last chance to lock in the America 250 rate before it ends tonight."
    : "Build your AI advantage with 50% off all AI Prompt Dojo paid subscriptions.";
  return (
    <div
      data-testid="am250-pricing-banner"
      className="relative overflow-hidden rounded-2xl p-5 md:p-6 mb-5 md:mb-7"
      style={{
        background: "linear-gradient(120deg, #0a0f1f 0%, #1e1b4b 50%, #0a0f1f 100%)",
        border: "1px solid rgba(212, 175, 55, 0.30)",
        color: "#fff",
      }}
    >
      <div
        className="absolute inset-x-0 top-0 h-[2px]"
        style={{ background: "linear-gradient(90deg, #B91C1C 0%, #2563EB 50%, #D4AF37 100%)" }}
      />
      <div className="relative flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-widest"
              style={{
                background: "rgba(212, 175, 55, 0.15)",
                color: "#D4AF37",
                border: "1px solid rgba(212, 175, 55, 0.45)",
              }}
            >
              <Sparkles size={11} /> America 250
            </span>
            {finalDay && (
              <span
                className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest"
                style={{ background: "#B91C1C", color: "#fff" }}
              >
                Ends Tonight
              </span>
            )}
          </div>
          <h3
            className="font-display tracking-tight"
            style={{ fontSize: "clamp(1.15rem, 2.4vw, 1.6rem)" }}
            data-testid="am250-pricing-headline"
          >
            {headline}
          </h3>
          <p className="text-sm mt-1" style={{ color: "rgba(255,255,255,0.78)" }} data-testid="am250-pricing-body">
            {body}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div
            className="px-3 py-1.5 rounded-lg font-display tracking-tight"
            style={{
              background: "rgba(255, 255, 255, 0.06)",
              border: "1px solid rgba(255, 255, 255, 0.10)",
              fontSize: "0.82rem",
            }}
          >
            <span className="font-semibold text-2xl" style={{ color: "#D4AF37" }}>50%</span>
            <span className="opacity-80 ml-1">off</span>
          </div>
          <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.65)" }}>
            Code {c.code || "AMERICA250"}
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Checkout confirmation strip ────────────────────────────────────────────
// Shown next to the price summary when the AMERICA250 discount is applied.
export function AmericaPromoCheckoutConfirm({ code, applied }) {
  if (!applied) return null;
  const isAm = (code || "").toUpperCase() === "AMERICA250";
  if (!isAm) return null;
  return (
    <div
      data-testid="am250-checkout-confirm"
      className="rounded-xl p-3 sm:p-4"
      style={{
        background: "linear-gradient(135deg, rgba(10,15,31,0.96) 0%, rgba(28,19,48,0.92) 100%)",
        border: "1px solid rgba(212, 175, 55, 0.40)",
        color: "#fff",
      }}
    >
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest" style={{ color: "#D4AF37" }}>
        <Sparkles size={12} /> Your America 250 Discount Is Applied
      </div>
      <p className="text-sm mt-1" style={{ color: "rgba(255,255,255,0.82)" }}>
        Start training the AI skill that creates speed, leverage, and better execution.
      </p>
      <div className="mt-2 inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[11px] font-mono font-semibold"
        style={{ background: "rgba(212,175,55,0.15)", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.40)" }}
      >
        50% off applied
      </div>
    </div>
  );
}

// ─── Referral copy override (used inside Refer page) ───────────────────────
// Drop-in text snippet for the referral hero when the campaign is active.
export function useAmericaReferralCopy() {
  const c = useCampaign();
  if (!c.active) return null;
  return {
    headline: c.is_final_day
      ? "Last chance — share AI Prompt Dojo and extend the America 250 rate"
      : "Share AI Prompt Dojo with a friend",
    body: "Share AI Prompt Dojo with a friend and both of you can extend the America 250 rate.",
  };
}


// ─── Festive birthday hat on the brand logo ────────────────────────────────
// Tasteful SVG hat that sits at the top-left of the brand mark. Used in the
// Header during the campaign window. Renders nothing when the campaign is
// inactive — no visual debt off-season.
export function FestiveBirthdayHat({ size = 22, className = "", style = {} }) {
  const c = useCampaign();
  if (!c.active) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      aria-hidden
      className={className}
      style={{
        // Positioned by the parent — leave layout to the caller.
        ...style,
      }}
      data-testid="am250-birthday-hat"
    >
      {/* Pom-pom (gold) */}
      <circle cx="16" cy="3.5" r="2.2" fill="#D4AF37" />
      <circle cx="16" cy="3.5" r="2.2" fill="url(#hatPom)" opacity="0.4" />
      {/* Hat body — navy gradient */}
      <path
        d="M16 5.5 L24.5 22 L7.5 22 Z"
        fill="url(#hatBody)"
        stroke="rgba(212,175,55,0.55)"
        strokeWidth="0.6"
        strokeLinejoin="round"
      />
      {/* Red stripe */}
      <path
        d="M11.4 14 L20.6 14 L21.7 16.2 L10.3 16.2 Z"
        fill="#B91C1C"
        opacity="0.95"
      />
      {/* Gold trim along the bottom */}
      <path
        d="M7.5 22 L24.5 22"
        stroke="#D4AF37"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      {/* Two confetti dots */}
      <circle cx="26.5" cy="13" r="1.0" fill="#2563EB" />
      <circle cx="5.5"  cy="17" r="0.9" fill="#D4AF37" />
      <defs>
        <linearGradient id="hatBody" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stopColor="#1e1b4b" />
          <stop offset="100%" stopColor="#0a0f1f" />
        </linearGradient>
        <radialGradient id="hatPom" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%"  stopColor="#fff" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0" />
        </radialGradient>
      </defs>
    </svg>
  );
}


// ─── Festive confetti page background ──────────────────────────────────────
// Static, CSS-only — no JS animation cost. Renders 36 small dots in red /
// electric-blue / gold scattered across a fixed full-viewport layer at very
// low opacity. Sits BEHIND every other element via z-index: 0 + pointer-
// events: none so it never interferes with clicks, hover, or readability.
// Auto-hides when the campaign is inactive.
const _CONFETTI_DOTS = (() => {
  // Deterministic pseudo-random scatter so SSR + hydration match. Seed +
  // a tiny LCG so we don't need a library.
  let s = 0x1f250;
  const rand = () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
  const palette = ["#B91C1C", "#2563EB", "#D4AF37", "#fff"];
  const out = [];
  for (let i = 0; i < 36; i++) {
    out.push({
      x: +(rand() * 100).toFixed(2),       // %
      y: +(rand() * 100).toFixed(2),       // %
      size: +(2 + rand() * 4).toFixed(2),  // px
      rot: +(rand() * 360).toFixed(2),     // deg (for square shapes)
      color: palette[Math.floor(rand() * palette.length)],
      shape: rand() > 0.55 ? "rect" : "circle",
      opacity: +(0.12 + rand() * 0.20).toFixed(2),
    });
  }
  return out;
})();

export function FestiveConfetti() {
  const c = useCampaign();
  if (!c.active) return null;
  return (
    <div
      aria-hidden
      data-testid="am250-festive-confetti"
      className="pointer-events-none fixed inset-0 overflow-hidden"
      style={{ zIndex: 0 }}
    >
      {_CONFETTI_DOTS.map((d, i) => (
        <span
          key={i}
          style={{
            position: "absolute",
            left: `${d.x}%`,
            top: `${d.y}%`,
            width: `${d.size}px`,
            height: `${d.size * (d.shape === "rect" ? 0.45 : 1)}px`,
            background: d.color,
            opacity: d.opacity,
            transform: `rotate(${d.rot}deg)`,
            borderRadius: d.shape === "circle" ? "50%" : "1px",
          }}
        />
      ))}
    </div>
  );
}
